/**
 * Compliance Data Context
 * Provides shared compliance data to all components without duplicate API calls
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUserContext } from '@/hooks';
import {
  organizeRequirementsByScope,
  computeDashboardSections,
  ComplianceDataState,
  ComplianceSubject,
  AssistingRole,
  TargetSubjectScope,
  TargetSubjectAreas,
} from '@/utils/complianceSectionUtils';
import {
  fetchRequirementCompliance,
  fetchServiceProviderCompliance,
  RequirementComplianceResponse,
  ComplianceRequirementsDataAdapter,
} from '@/services/complianceAssessmentService';
import {
  locationService,
  type State as LocationState,
  type District as LocationDistrict,
  type DispatchArea as LocationDispatchArea,
} from '@/services/locationService';
export type { Role, RequirementWithStatus, Scope } from '@/services/complianceAssessmentService';

// ============================================================================
// Types
// ============================================================================

export type ComplianceStatusType =
  | 'NotStarted'
  | 'Passed'
  | 'Failed'
  | 'Warning'
  | 'NotApplicable'
  | 'Expired'
  | 'Submitted'
  | 'Compliant'
  | 'Rejected';

export type ScopeType = 'EverDriven' | 'State' | 'District' | 'Vehicle' | 'Shared';

// All shared types moved to complianceSectionUtils; re-export so callers importing from context still work
export type {
  RequirementsByScope,
  DashboardAlerts,
  DashboardSection,
  ComplianceDataState,
} from '@/utils/complianceSectionUtils';

// ============================================================================
// Helper Functions
// ============================================================================

// getAlertsFromScope and computeDashboardSections moved to complianceSectionUtils

// ============================================================================
// Context
// ============================================================================

const ComplianceDataContext = createContext<ComplianceDataState | null>(null);

/**
 * Build a stable string key from a list of ids. Sorted + de-duped so callers
 * that treat the list as a SET (e.g. the target-subject-scope fetch effect)
 * don't refire when the same ids arrive in a different order.
 */
function toIdSetKey(ids: string[] | undefined): string {
  if (!ids || ids.length === 0) return '';
  // Sonar S2871: bare `.sort()` on strings is engine-dependent — pass an
  // explicit comparator so the produced key is stable across environments.
  return [...new Set(ids)].sort((a, b) => a.localeCompare(b)).join(',');
}

// ============================================================================
// Provider
// ============================================================================

interface ComplianceDataProviderProps {
  children: React.ReactNode;
  /**
   * Identity whose requirements to fetch. Omit for the logged-in user
   * (Requirement Overview); pass an explicit subject for the SP-assisted
   * Compliance Management screen.
   */
  subject?: ComplianceSubject;
  /**
   * Which assist role this screen represents (SP today, FO planned). Drives
   * the "Can Complete" column header, filter label, and predicate so the same
   * view code serves both screens. Null/omitted on Requirement Overview.
   */
  assistingRole?: AssistingRole | null;
  /**
   * Geographic scope of the subject (driver / monitor) being managed. Passed
   * from Compliance Status Lookup so Add / Edit Vehicle dialogs constrain
   * their state / district / dispatch-area pickers to the driver's authorised
   * areas rather than the logged-in user's own scope. Omitted on legacy
   * flows where the login user IS the subject.
   */
  targetSubjectScope?: TargetSubjectScope;
}

export function ComplianceDataProvider({
  children,
  subject,
  assistingRole = null,
  targetSubjectScope,
}: Readonly<ComplianceDataProviderProps>) {
  const [rawData, setRawData] = useState<RequirementComplianceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { userId, roles } = useUserContext();
  const effectiveSubject: ComplianceSubject = subject ?? { id: userId, roles };
  const assessmentEntityId = effectiveSubject.id;
  const subjectKind = effectiveSubject.kind ?? 'user';

  // Use refs to prevent duplicate fetches and handle unmount scenarios
  const fetchStartedRef = useRef(false);
  const dataFetchedRef = useRef(false);
  const isMountedRef = useRef(false);

  const fetchData = useCallback(async () => {
    // Prevent duplicate fetches while a request is already in flight
    if (fetchStartedRef.current && !dataFetchedRef.current) {
      return; // Already fetching
    }

    // Don't start a fetch if component is not mounted
    if (!isMountedRef.current) {
      return;
    }

    try {
      fetchStartedRef.current = true;
      setLoading(true);
      setError(null);
      const data = subjectKind === 'serviceProvider'
        ? await fetchServiceProviderCompliance(assessmentEntityId, { enablePagination: false })
        : await fetchRequirementCompliance(assessmentEntityId, { enablePagination: false });

      // Check if still mounted before updating state
      if (!isMountedRef.current) {
        return;
      }

      setRawData(data);
      dataFetchedRef.current = true;
    } catch (err) {
      // Check if still mounted before updating state
      if (!isMountedRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to fetch compliance data';
      setError(message);
    } finally {
      // Check if still mounted before updating state
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [assessmentEntityId, subjectKind]);

  // Reset the de-dupe refs whenever the subject id or kind changes so the next
  // fetch effect fires for the new subject. Without this the de-dupe guards
  // (intended only for React StrictMode remounts) silently keep serving
  // stale data when the provider is re-rendered with a different `subject`.
  useEffect(() => {
    fetchStartedRef.current = false;
    dataFetchedRef.current = false;
  }, [assessmentEntityId, subjectKind]);

  // Fetch data on mount with proper cleanup
  useEffect(() => {
    isMountedRef.current = true;

    // Only fetch if we haven't fetched yet and no fetch is in progress
    if (!dataFetchedRef.current && !fetchStartedRef.current) {
      fetchData();
    }

    return () => {
      // Mark as unmounted - do NOT reset fetchStartedRef here
      // to prevent duplicate fetches during React StrictMode remounts
      isMountedRef.current = false;
    };
  }, [fetchData]);

  // Compute dashboard sections from raw data
  const dashboardSections = useMemo(() => {
    if (!rawData?.scopes) return [];
    return computeDashboardSections(rawData.scopes);
  }, [rawData]);

  // Organize requirements by scope
  const requirementsByScope = useMemo(() => {
    if (!rawData?.scopes) return {};
    return organizeRequirementsByScope(rawData.scopes);
  }, [rawData]);

  // Entity summary
  const entitySummary = useMemo(() => {
    return rawData?.entitySummary || null;
  }, [rawData]);

  // Refetch function (resets refs to allow new fetch)
  const refetch = useCallback(async () => {
    fetchStartedRef.current = false;
    dataFetchedRef.current = false;
    await fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Target-subject scope resolution (Compliance Status Lookup flow)
  //
  // When `targetSubjectScope` is supplied, resolve the driver's authorised
  // state / district / dispatch-area IDs into full LocationState /
  // LocationDistrict / LocationDispatchArea objects ONCE per screen. Both
  // AddNewVehicleDialog and EditVehicleDialog mount unconditionally inside
  // ComplianceRequirementsView and previously fired identical fetches from
  // their own hook instances — doing it here at the shared provider layer
  // collapses that back down to a single pair of calls fired in parallel.
  // ---------------------------------------------------------------------------
  const [targetStates, setTargetStates] = useState<LocationState[]>([]);
  const [targetDistricts, setTargetDistricts] = useState<LocationDistrict[]>([]);
  const [targetDispatchAreas, setTargetDispatchAreas] = useState<LocationDispatchArea[]>([]);
  const [targetAreasLoading, setTargetAreasLoading] = useState<boolean>(!!targetSubjectScope);

  // Stringify id lists so the effect only re-runs when the SET of ids actually
  // changes (not on every parent re-render of ComplianceManagementView).
  // Copilot review (PR 307): sort + dedupe before joining so the key is
  // order-insensitive — the same ids in a different order (or with duplicates
  // from a parent re-render quirk) must not retrigger the fetch.
  const targetStateKey = toIdSetKey(targetSubjectScope?.stateIds);
  const targetDistrictKey = toIdSetKey(targetSubjectScope?.districtIds);
  const targetDispatchAreaKey = toIdSetKey(targetSubjectScope?.dispatchAreaIds);

  useEffect(() => {
    if (!targetSubjectScope) {
      setTargetStates([]);
      setTargetDistricts([]);
      setTargetDispatchAreas([]);
      setTargetAreasLoading(false);
      return;
    }

    const noStatesOrDistricts =
      targetSubjectScope.stateIds.length === 0 && targetSubjectScope.districtIds.length === 0;

    let cancelled = false;
    setTargetAreasLoading(true);

    // Fire in parallel: /Districts/locations-by-ids for the driver's states +
    // districts, /DispatchAreas/by-state for the driver's states (then filter
    // to the driver's DA subset). One wall-clock round-trip; both dialogs
    // read the resolved lists straight off the context afterwards.
    const locationsPromise = noStatesOrDistricts
      ? Promise.resolve({ states: [] as LocationState[], districts: [] as LocationDistrict[] })
      : locationService.fetchLocationsByIds(
          targetSubjectScope.stateIds,
          targetSubjectScope.districtIds
        );

    const dispatchAreasPromise =
      targetSubjectScope.stateIds.length === 0
        ? Promise.resolve([] as LocationDispatchArea[])
        : locationService.fetchDispatchAreasByStateIds(targetSubjectScope.stateIds);

    Promise.all([locationsPromise, dispatchAreasPromise])
      .then(([locations, allDaInStates]) => {
        if (cancelled) return;
        const allowedDaIds = new Set(targetSubjectScope.dispatchAreaIds);
        const filteredDispatchAreas = allDaInStates.filter((da) => allowedDaIds.has(da.id));
        setTargetStates(locations.states);
        setTargetDistricts(locations.districts);
        setTargetDispatchAreas(filteredDispatchAreas);
      })
      .catch((err) => {
        if (cancelled) return;
        // Preserve the surface: leave existing lists empty rather than throwing
        // so vehicle pickers render "no scope" instead of tearing down the view.
        console.error('[ComplianceDataProvider] Failed to resolve target subject areas:', err);
        setTargetStates([]);
        setTargetDistricts([]);
        setTargetDispatchAreas([]);
      })
      .finally(() => {
        if (!cancelled) setTargetAreasLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Effect keys off the string-joined id lists so parent re-renders that
    // recreate the array reference (but not its contents) don't refire.
  }, [targetStateKey, targetDistrictKey, targetDispatchAreaKey, !!targetSubjectScope]);

  const targetSubjectAreas: TargetSubjectAreas | undefined = targetSubjectScope
    ? {
        states: targetStates,
        districts: targetDistricts,
        dispatchAreas: targetDispatchAreas,
        isLoading: targetAreasLoading,
      }
    : undefined;

  const value: ComplianceDataState = {
    rawData,
    dashboardSections,
    requirementsByScope,
    entitySummary,
    loading,
    error,
    refetch,
    subject: effectiveSubject,
    assistingRole,
    ...(targetSubjectAreas && { targetSubjectAreas }),
  };

  return (
    <ComplianceDataContext.Provider value={value}>
      {children}
    </ComplianceDataContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useComplianceDataContext(): ComplianceDataState {
  const context = useContext(ComplianceDataContext);
  if (!context) {
    throw new Error('useComplianceDataContext must be used within a ComplianceDataProvider');
  }
  return context;
}

/**
 * Non-throwing variant. Returns `null` when called outside a
 * ComplianceDataProvider. Used by optional consumers (e.g. vehicle dialogs
 * that also render on the Manage tab where no provider is present).
 */
export function useOptionalComplianceDataContext(): ComplianceDataState | null {
  return useContext(ComplianceDataContext);
}

// Re-export for convenience
export { ComplianceRequirementsDataAdapter };
